import { useEffect, useRef, useState } from 'react';
import { useDarkMode } from '@/hooks/useDarkMode';

import { SlidersHorizontal } from 'lucide-react';
import ArchetypeCarousel from '@/components/browse/ArchetypeCarousel';

export default function StickyFilterBar({
  isScrolled,
  filters,
  setFilters,
  showFilters,
  setShowFilters,
  hasActiveFilters,
  categories = [],
  browseAll = false,
  onToggleBrowseAll,
  archetypes = [],
  activeArchetype,
  onArchetypeChange,
  children,
}) {
  const darkMode = useDarkMode();

  // Direction-aware visibility: hide while scrolling DOWN, but ONLY once the
  // bar is actually pinned (stuck). Before it sticks, translating it would
  // lift it over the content above it (CheckoutGuide) and cause overlap.
  // A static sentinel just before the bar tells us its natural flow position.
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const sentinelRef = useRef(null);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = Math.max(0, window.scrollY);
        const delta = y - lastY.current;
        if (Math.abs(delta) < 6) return; // ignore micro-jitter
        const stickyTop = window.innerWidth >= 640 ? 80 : 64;
        const stuck = sentinelRef.current
          ? sentinelRef.current.getBoundingClientRect().top <= stickyTop
          : false;
        if (delta > 0 && stuck) setHidden(true);
        else setHidden(false);
        lastY.current = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const isHidden = hidden && !showFilters;

  const handleChipClick = (categoryName) => {
    if (filters.category === categoryName) {
      setFilters({ ...filters, category: '' });
    } else {
      setFilters({ ...filters, category: categoryName });
    }
  };

  const categoryNames = categories
    .map((c) => (typeof c === 'string' ? c : c?.name))
    .filter(Boolean);

  // Category chips only appear inside the Curated archetype. Non-Curated
  // worlds (e.g. Experiences) have their own content structure and would be
  // confused by product-category chips.
  const showCategoryChips =
    (!activeArchetype || activeArchetype === 'curated') && categoryNames.length > 0;

  return (
    <>
    <div ref={sentinelRef} aria-hidden="true" />
    <div
      className="sticky top-16 sm:top-20 z-40 transition-colors"
      style={{
        background: darkMode.isDark ? '#2D4354' : 'white',
        borderBottom: `1px solid ${darkMode.isDark ? '#3A3B45' : '#E5E5E5'}`,
        boxShadow: isScrolled && !isHidden ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
        transform: isHidden ? 'translateY(-130%)' : 'translateY(0)',
        transition: 'transform 0.28s ease, box-shadow 0.3s ease, background-color 0.3s ease'
      }}
      data-testid="sticky-filter-bar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        {/* Row 1: Archetype carousel (primary discovery) + Filter icon */}
        <div className="flex flex-row items-center gap-2 sm:gap-3 w-full">
          <div className="flex-1 min-w-0">
            <ArchetypeCarousel
              archetypes={archetypes}
              activeId={activeArchetype}
              onSelect={onArchetypeChange}
            />
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-label={showFilters ? 'Close filters and search' : 'Open filters and search'}
            className="relative flex items-center justify-center rounded-full transition-all flex-shrink-0 w-11 h-11"
            style={{
              background: showFilters
                ? (darkMode.isDark ? '#FED7A5' : '#2D4354')
                : 'transparent',
              color: showFilters
                ? (darkMode.isDark ? '#20212B' : '#FED7A5')
                : (darkMode.isDark ? '#FED7A5' : '#2D4354'),
              border: `2px solid ${darkMode.isDark ? '#FED7A5' : '#2D4354'}`
            }}
            data-testid="browse-filter-toggle-button"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {hasActiveFilters && !showFilters && (
              <span
                className="w-2.5 h-2.5 rounded-full absolute -top-0.5 -right-0.5"
                style={{ background: '#FED7A5', border: darkMode.isDark ? 'none' : '2px solid white' }}
              />
            )}
          </button>
        </div>

        {/* Row 2 (Curated only): Category chip rail */}
        {showCategoryChips && (
          <div className="pt-3" data-testid="category-rail-wrapper">
            <div className="flex items-center gap-3">
              <span
                className="text-[10px] uppercase tracking-[0.18em] font-semibold flex-shrink-0 hidden sm:inline"
                style={{
                  color: darkMode.isDark
                    ? 'rgba(254,215,165,0.55)'
                    : 'rgba(83,65,69,0.55)',
                }}
              >
                Browse by
              </span>
              <div
                className="flex gap-2 overflow-x-auto flex-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                data-testid="category-chip-rail"
              >
                {/* "All" = Browse All: show every product in one mixed/
                    random stream (no category sections). Selected = flat
                    view; deselect to return to the category-organized
                    sections. */}
                <CategoryChip
                  label="All"
                  active={browseAll}
                  onClick={() => onToggleBrowseAll && onToggleBrowseAll(!browseAll)}
                  darkMode={darkMode}
                  testid="category-chip-All"
                />
                {categoryNames.map((cat) => (
                  <CategoryChip
                    key={cat}
                    label={cat}
                    active={filters.category === cat}
                    onClick={() => handleChipClick(cat)}
                    darkMode={darkMode}
                    testid={`category-chip-${cat}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {children}
    </div>
    </>
  );
}

function CategoryChip({ label, active, onClick, darkMode, testid }) {
  return (
    <button
      onClick={onClick}
      className="snap-start flex-shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors hover:transform-none"
      style={{
        background: active ? '#FED7A5' : 'transparent',
        color: active
          ? '#20212B'
          : (darkMode.isDark ? '#FED7A5' : '#534145'),
        border: `1.5px solid ${active ? '#FED7A5' : (darkMode.isDark ? 'rgba(254,215,165,0.4)' : 'rgba(83, 65, 69, 0.25)')}`
      }}
      data-testid={testid}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
